import { Alert, AlertColor, Box, Card, Grid, Snackbar, Stack, Tooltip, Typography } from '@mui/material';
import BackIcon from '../../../icons/backIcon.svg';
import { useHistory, useParams } from 'react-router';
import { announcementStyles } from './CustomStyles';
import { AnnouncementState, cloneAnnouncement, getAnnouncementEvent, resetCloneAnnouncementState, selectAnnouncementsState } from '../sagas/announcementSlice';
import { useDispatch, useSelector } from 'react-redux';
import BadRequestIcon from '../../../icons/Badrequesterror.svg';
import Error500 from '../../../icons/505Error.svg';
import SpinnerWithNoConfirmation from '../../../shared/Modal/spinnerWithNoConfirmation';
import { useEffect, useMemo, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import { AnnouncementMethods, AnnouncementRecipientDto } from '@inosio/payments-sdk';
import CheckMarkIcon from '../../../icons/CheckMark.svg';
import IncompleteIcon from '../../../icons/IncompleteIcon.svg';
import { convertToHumanReadable, pluralize } from '../../../utils/string-util';
import { format } from 'date-fns';

export const SentAnnouncementSummary = () => {
  const { uuid: eventUuid } = useParams<{ uuid: string }>();
  const dispatch = useDispatch();

  const {
    isGettingAnnouncementEvent,
    AnnouncementEventError,
    announcementEvent,
    cloneAnnouncementDetail,
    isCloningAnnouncement,
    isCloningAnnouncementFailed,
    isCloningAnnouncementSuccess,
    CloneAnnouncementError,
  }: AnnouncementState = useSelector(selectAnnouncementsState);
  const [snackBar, setSnackBar] = useState<{ severity: AlertColor; message?: string } | null>(null);
  const history = useHistory();
  const classes = announcementStyles();
  const labelStyle = {
    fontSize: '14px',
    fontWeight: 'bold',
  };

  const handleCloneAnnouncement = () => {
    if (announcementEvent.TemplateUuid) {
      dispatch(
        cloneAnnouncement({
          templateUuid: announcementEvent.TemplateUuid,
          announcementMethod: AnnouncementMethods.Email,
        }),
      );
    }
  };

  const getErrorIcon = (statusCode: number | undefined) => {
    if (String(statusCode)[0] === '4') {
      return BadRequestIcon;
    } else {
      return Error500;
    }
  };

  useEffect(() => {
    fetchAnnouncementEvent();
  }, [dispatch, eventUuid]);

  const fetchAnnouncementEvent = () => {
    dispatch(
      getAnnouncementEvent({
        eventUuid: eventUuid,
      }),
    );
  };

  useEffect(() => {
    if (isCloningAnnouncementFailed && CloneAnnouncementError.statusCode) {
      setSnackBar({
        severity: 'error',
        message: CloneAnnouncementError.message,
      });
      dispatch(resetCloneAnnouncementState());
    }
  }, [CloneAnnouncementError, isCloningAnnouncementFailed]);

  useEffect(() => {
    if (isCloningAnnouncementSuccess && cloneAnnouncementDetail.TemplateUuid) {
      history.push(`/pmui/announcements/${cloneAnnouncementDetail.TemplateUuid}/details`);
      dispatch(resetCloneAnnouncementState());
    }
  }, [isCloningAnnouncementSuccess, cloneAnnouncementDetail]);

  const handleAlertClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackBar(null);
  };

  const groupByEntityType = (recipients: AnnouncementRecipientDto[]): Record<string, AnnouncementRecipientDto[]> => {
    return recipients.reduce((grouped, recipient) => {
      const { EntityType } = recipient;
      if (!grouped[EntityType]) {
        grouped[EntityType] = [];
      }
      grouped[EntityType].push(recipient);
      return grouped;
    }, {} as Record<string, AnnouncementRecipientDto[]>);
  };

  const groupedRecipients = useMemo(() => {
    return groupByEntityType(announcementEvent.Recipients || []);
  }, [announcementEvent.Recipients]);

  return (
    <Grid item xs={12} sx={{ mt: 3.5 }}>
      <Snackbar anchorOrigin={{ horizontal: 'center', vertical: 'top' }} open={!!snackBar} autoHideDuration={4000} onClose={handleAlertClose}>
        <Alert onClose={handleAlertClose} severity={snackBar?.severity}>
          {snackBar?.message}
        </Alert>
      </Snackbar>
      <Card
        sx={{
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          height: 'calc( 100vh - 144px )',
          // pb: '64px',
        }}
        style={{ position: 'relative' }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', height: '70px', alignItems: 'center', px: 2, borderBottom: '1px solid #EEEEEE', pb: 2 }}>
          <Box sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }} onClick={() => history.goBack()}>
            <img
              style={{
                width: '20px',
                height: '20px',
              }}
              src={BackIcon}
              alt="backicon"
            />
            <Typography sx={{ ml: 1, color: '#39A2DB' }} className={classes.labelText}>
              {`Back`}
            </Typography>
          </Box>
          <Box>
            <LoadingButton loading={isCloningAnnouncement} type="button" className={classes.wizardNextBtn} onClick={handleCloneAnnouncement}>
              <Typography className={classes.cloneButtonText} sx={{ margin: '0 10px' }}>
                Clone Announcement
              </Typography>
            </LoadingButton>
          </Box>
        </Box>

        {AnnouncementEventError.statusCode ? (
          <Grid container direction="row" spacing={2} alignItems="center" justifyContent="center" sx={{ height: '100vh' }}>
            <Grid item display="flex" flexDirection="column" alignItems="center">
              <Grid item display="flex" justifyContent="center" sx={{ marginLeft: '5%' }}>
                <img src={getErrorIcon(AnnouncementEventError?.statusCode)} alt={String(AnnouncementEventError?.statusCode)} style={{ height: '35vh' }} />
              </Grid>
              <Grid item justifyContent="center">
                <Typography
                  sx={{
                    fontFamily: 'Work Sans',
                    fontStyle: 'normal',
                    fontWeight: 700,
                    fontSize: '40px',
                    lineHeight: '23px',
                    color: '#2E82AF',
                    textAlign: 'center',
                    marginTop: '10px',
                  }}
                >
                  {AnnouncementEventError?.statusCode}
                </Typography>
              </Grid>
              {AnnouncementEventError?.statusCode === 404 && (
                <Grid mt={2} item justifyContent="center">
                  <Typography
                    sx={{
                      fontFamily: 'Work Sans',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      fontSize: '20px',
                      lineHeight: '23px',
                      color: '#9E9E9E',
                      textAlign: 'center',
                      marginTop: '10px',
                    }}
                  >
                    Page not found.
                  </Typography>
                </Grid>
              )}
              <Grid item justifyContent="center" mt={2} xs={8}>
                <Typography
                  sx={{
                    fontFamily: 'Work Sans',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '23px',
                    color: '#9E9E9E',
                    textAlign: 'center',
                    marginTop: '10px',
                  }}
                >
                  {AnnouncementEventError?.message?.length === 0 ? '' : AnnouncementEventError?.message}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        ) : isGettingAnnouncementEvent ? (
          <Stack
            justifyContent="center"
            alignItems="center"
            style={{
              height: '1100px',
            }}
          >
            <SpinnerWithNoConfirmation />
          </Stack>
        ) : (
          <Box sx={{ padding: 2, marginTop : 1 }}>
            <Grid container spacing={3}>
              <Grid item xs={8} borderRight="2px solid #EEEEEE" paddingRight={"24px"}>
                <Grid container spacing={1} sx={{ marginBottom: 2 }}>
                  <Grid item xs={2}>
                    <Typography sx={labelStyle}>Send From:</Typography>
                  </Grid>
                  <Grid item xs={10}>
                    <Typography variant="body1">{announcementEvent?.SenderIdentity?.Email}</Typography>
                  </Grid>
                </Grid>
                <Grid container spacing={1} sx={{ marginBottom: 2 }}>
                  <Grid item xs={2}>
                    <Typography sx={labelStyle}>Sent On:</Typography>
                  </Grid>
                  <Grid item xs={10}>
                    {announcementEvent.SentDate ? (
                      <Typography variant="body1">
                        {format(new Date(announcementEvent.SentDate)!, `MM/dd/yyyy hh:mm a`)}
                      </Typography>
                    ) : null}
                  </Grid>
                </Grid>
                <Grid container spacing={1} sx={{ marginBottom: 2 }}>
                  <Grid item xs={2}>
                    <Typography sx={labelStyle}>Subject:</Typography>
                  </Grid>
                  <Grid item xs={10}>
                    <Typography variant="body1">{announcementEvent?.Subject}</Typography>
                  </Grid>
                </Grid>
                <Grid container spacing={1} sx={{ marginBottom: 2 }}>
                  <Grid item xs={2}>
                    <Typography sx={labelStyle}>Message:</Typography>
                  </Grid>
                  <Grid item xs={10}>
                    <Typography variant="body1" dangerouslySetInnerHTML={{ __html: announcementEvent?.Body }}></Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={4} sx={{ flexGrow: 1, overflowY: 'auto', flexDirection: 'column', height: 'calc(100vh - 220px)' }}>
                <Typography sx={labelStyle}>Sent To:</Typography>
                {announcementEvent?.RecipientOptions?.map((recipient) => (
                  <Box sx={{ marginBottom: 0 }} key={recipient}>
                    <Typography variant="body2">{convertToHumanReadable(recipient)}</Typography>
                  </Box>
                ))}
                {/* Scrollable container */}
                <Box sx={{ paddingRight: '10px' }}>
                  {Object.keys(groupedRecipients).map((entityType) => (
                    <Box key={entityType} sx={{ marginBottom: 3 }}>
                      <Typography
                        sx={{
                          ...labelStyle,
                          marginTop: '10px',
                        }}
                      >
                        {pluralize(entityType)}:
                      </Typography>
                      {groupedRecipients[entityType].map((recipient) => (
                        <Box sx={{ marginBottom: 1, display: 'flex', alignItems: 'center' }} key={recipient.RecipientUuid}>
                          <Tooltip placement="left" title={recipient.IsSuccessful ? 'Successfully sent.' : 'Not delivered'}>
                            <img
                              style={{
                                width: '12px',
                                height: '9px',
                                marginRight: '10px',
                              }}
                              src={recipient.IsSuccessful ? CheckMarkIcon : IncompleteIcon}
                              alt={recipient.IsSuccessful ? 'Sent' : 'NotDelivered'}
                            />
                          </Tooltip>
                          <Typography variant="body2">{recipient.RecipientContact}</Typography>
                        </Box>
                      ))}
                    </Box>
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}
      </Card>
    </Grid>
  );
};
